import React, { useState } from 'react';
import { Download, Upload, ArrowLeftRight, PieChart, ExternalLink } from 'lucide-react';
import { assetHoldings } from '../../utils/portfolioData';
interface BalancesSectionProps {
  accountType: 'main' | 'futures' | 'inverse-futures' | 'earn';
}
export function BalancesSection({
  accountType
}: BalancesSectionProps) {
  const [activeTab, setActiveTab] = useState<'all' | 'spot' | 'margin'>('all');
  // Mock collateral data for futures accounts
  const collateralAssets = [{
    symbol: 'USD',
    name: 'US Dollar',
    logo: 'https://cryptologos.cc/logos/usd-coin-usdc-logo.svg',
    balance: 6.0,
    currentPrice: 0.7975,
    estValue: 4.78,
    collateralWeight: 100.0,
    estCollateralValue: 4.78,
    percentOfCollateral: 99.93
  }];
  if (accountType === 'futures' || accountType === 'inverse-futures') {
    return <div className="bg-background-secondary border border-border-subtle rounded-[20px] overflow-hidden" data-id="element-1050">
        {/* Header */}
        <div className="border-b border-border-subtle px-6 py-4" data-id="element-1051">
          <div className="flex items-center justify-between" data-id="element-1052">
            <h3 className="text-lg font-semibold text-text-primary" data-id="element-1053">
              Collateral assets
            </h3>
            <button className="px-3 py-1.5 text-xs text-text-secondary hover:text-text-primary border border-border-subtle hover:border-border-default rounded-lg transition-colors" data-id="element-1054">
              Convert small balances
            </button>
          </div>
        </div>

        {/* Empty State or Collateral Table */}
        {accountType === 'inverse-futures' || collateralAssets.length === 0 ? <div className="flex flex-col items-center justify-center py-16" data-id="element-1055">
            <div className="w-16 h-16 rounded-full bg-background-elevated flex items-center justify-center mb-4" data-id="element-1056">
              <PieChart size={32} className="text-text-tertiary" data-id="element-1057" />
            </div>
            <p className="text-text-secondary text-sm" data-id="element-1058">No collateral assets</p>
          </div> : <div className="overflow-x-auto" data-id="element-1059">
            <table className="w-full" data-id="element-1060">
              <thead className="bg-background-elevated/50" data-id="element-1061">
                <tr className="border-b border-border-subtle" data-id="element-1062">
                  <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-left" data-id="element-1063">
                    Asset
                  </th>
                  <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-1064">
                    Balance
                  </th>
                  <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-1065">
                    Current Price
                  </th>
                  <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-1066">
                    Est. Value
                  </th>
                  <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-1067">
                    Collateral Weight
                  </th>
                  <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-1068">
                    Est. Collateral Value
                  </th>
                  <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-1069">
                    % of Collateral Value
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-1070">
                {collateralAssets.map(asset => <tr key={asset.symbol} className="border-b border-border-subtle hover:bg-background-elevated/30 transition-colors" data-id="element-1071">
                    <td className="px-6 py-4" data-id="element-1072">
                      <div className="flex items-center gap-3" data-id="element-1073">
                        <img src={asset.logo} alt={asset.name} className="w-6 h-6 object-contain" data-id="element-1074" />
                        <div data-id="element-1075">
                          <div className="font-medium text-text-primary text-sm" data-id="element-1076">
                            {asset.name}
                          </div>
                          <div className="text-xs text-text-secondary" data-id="element-1077">
                            {asset.symbol}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-1078">
                      <div className="font-numbers text-sm text-text-primary" data-id="element-1079">
                        {asset.balance.toFixed(2)} {asset.symbol}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-1080">
                      <div className="font-numbers text-sm text-text-primary" data-id="element-1081">
                        {asset.currentPrice.toFixed(4)} CHF
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-1082">
                      <div className="font-numbers text-sm text-text-primary" data-id="element-1083">
                        {asset.estValue.toFixed(2)} CHF
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-1084">
                      <div className="font-numbers text-sm text-text-primary" data-id="element-1085">
                        {asset.collateralWeight.toFixed(2)}%
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-1086">
                      <div className="font-numbers text-sm text-text-primary font-medium" data-id="element-1087">
                        {asset.estCollateralValue.toFixed(2)} CHF
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-1088">
                      <div className="flex items-center justify-end gap-2" data-id="element-1089">
                        <div className="w-20 h-2 bg-background-elevated rounded-full overflow-hidden" data-id="element-1090">
                          <div className="h-full bg-success" style={{
                      width: `${asset.percentOfCollateral}%`
                    }} data-id="element-1091"></div>
                        </div>
                        <span className="font-numbers text-sm text-text-primary w-12 text-right" data-id="element-1092">
                          {asset.percentOfCollateral.toFixed(2)}%
                        </span>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>}
      </div>;
  }
  // Main account balances
  const holdings = assetHoldings.map(asset => {
    const amount = asset.spotAmount;
    const value = amount * asset.price;
    return {
      ...asset,
      amount,
      value
    };
  }).filter(asset => asset.amount > 0);
  return <div className="bg-background-secondary border border-border-subtle rounded-[20px] overflow-hidden" data-id="element-1093">
      {/* Header with Tabs */}
      <div className="border-b border-border-subtle" data-id="element-1094">
        <div className="flex items-center justify-between px-6 py-4" data-id="element-1095">
          <div className="flex items-center gap-1" data-id="element-1096">
            <button onClick={() => setActiveTab('all')} className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all ${activeTab === 'all' ? 'bg-background-elevated text-text-primary' : 'text-text-secondary hover:text-text-primary hover:bg-background-elevated/50'}`} data-id="element-1097">
              All
            </button>
            <button onClick={() => setActiveTab('spot')} className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all ${activeTab === 'spot' ? 'bg-background-elevated text-text-primary' : 'text-text-secondary hover:text-text-primary hover:bg-background-elevated/50'}`} data-id="element-1098">
              Spot
            </button>
            <button onClick={() => setActiveTab('margin')} className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all ${activeTab === 'margin' ? 'bg-background-elevated text-text-primary' : 'text-text-secondary hover:text-text-primary hover:bg-background-elevated/50'}`} data-id="element-1099">
              Margin
            </button>
          </div>
          <button className="px-3 py-1.5 text-xs text-text-secondary hover:text-text-primary border border-border-subtle hover:border-border-default rounded-lg transition-colors" data-id="element-1100">
            Convert small balances
          </button>
        </div>
      </div>

      {/* Balances Table */}
      <div className="overflow-x-auto" data-id="element-1101">
        <table className="w-full" data-id="element-1102">
          <thead className="bg-background-elevated/50" data-id="element-1103">
            <tr className="border-b border-border-subtle" data-id="element-1104">
              <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-left" data-id="element-1105">
                Asset
              </th>
              <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-1106">
                Balance
              </th>
              <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-1107">
                Current Price
              </th>
              <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-1108">
                Est. Value
              </th>
              <th className="px-6 py-3 text-xs font-label text-text-tertiary uppercase tracking-wider text-right" data-id="element-1109">
                Actions
              </th>
            </tr>
          </thead>
          <tbody data-id="element-1110">
            {holdings.map(asset => <tr key={asset.symbol} className="border-b border-border-subtle hover:bg-background-elevated/30 transition-colors" data-id="element-1111">
                <td className="px-6 py-4" data-id="element-1112">
                  <div className="flex items-center gap-3" data-id="element-1113">
                    <img src={asset.logo} alt={asset.name} className="w-6 h-6 object-contain" data-id="element-1114" />
                    <div data-id="element-1115">
                      <div className="font-medium text-text-primary text-sm" data-id="element-1116">
                        {asset.name}
                      </div>
                      <div className="text-xs text-text-secondary" data-id="element-1117">
                        {asset.symbol}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-right" data-id="element-1118">
                  <div className="font-numbers text-sm text-text-primary" data-id="element-1119">
                    {asset.amount.toLocaleString('en-US', {
                  minimumFractionDigits: asset.symbol === 'USDT' ? 2 : 8,
                  maximumFractionDigits: asset.symbol === 'USDT' ? 2 : 8
                })}
                  </div>
                  <div className="text-xs text-text-secondary" data-id="element-1120">
                    {asset.symbol}
                  </div>
                </td>
                <td className="px-6 py-4 text-right" data-id="element-1121">
                  <div className="font-numbers text-sm text-text-primary" data-id="element-1122">
                    {asset.price.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}{' '}
                    USD
                  </div>
                </td>
                <td className="px-6 py-4 text-right" data-id="element-1123">
                  <div className="font-numbers text-sm text-text-primary font-medium" data-id="element-1124">
                    {asset.value.toLocaleString('en-US', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}{' '}
                    USD
                  </div>
                </td>
                <td className="px-6 py-4 text-right" data-id="element-1125">
                  <div className="flex items-center justify-end gap-2" data-id="element-1126">
                    <button className="p-1.5 hover:bg-background-elevated rounded transition-colors" title="Deposit" data-id="element-1127">
                      <Download size={16} className="text-text-secondary hover:text-text-primary" data-id="element-1128" />
                    </button>
                    <button className="p-1.5 hover:bg-background-elevated rounded transition-colors" title="Withdraw" data-id="element-1129">
                      <Upload size={16} className="text-text-secondary hover:text-text-primary" data-id="element-1130" />
                    </button>
                    <button className="p-1.5 hover:bg-background-elevated rounded transition-colors" title="Convert" data-id="element-1131">
                      <ArrowLeftRight size={16} className="text-text-secondary hover:text-text-primary" data-id="element-1132" />
                    </button>
                  </div>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}